// ============================================================================
// Contacts API - Contact Management CRM
// Handles CRUD operations for contacts with local-first storage
// and optional cloud sync
// ============================================================================

import { contactRepository, syncQueueRepository } from '../storage/db'
import { authApi } from './auth.api'
import type { ContactRecord, ContactType, InteractionRecord } from '../types'

// ============================================================================
// Types
// ============================================================================

export interface CreateContactInput {
  name: string
  role: string
  email?: string
  phone?: string
  linkedInUrl?: string
  contactType: ContactType
  company?: string
  notes?: string
  linkedApplicationIds?: string[]
}

export interface UpdateContactInput {
  name?: string
  role?: string
  email?: string
  phone?: string
  linkedInUrl?: string
  contactType?: ContactType
  company?: string
  notes?: string
  linkedApplicationIds?: string[]
}

export interface ContactFilters {
  search?: string
  contactType?: ContactType
  company?: string
}

// ============================================================================
// Contacts API
// ============================================================================

export const contactsApi = {
  /**
   * Get all contacts
   */
  async getAll(): Promise<ContactRecord[]> {
    return contactRepository.getAll()
  },

  /**
   * Get contact by ID
   */
  async getById(id: string): Promise<ContactRecord | undefined> {
    return contactRepository.getById(id)
  },

  /**
   * Get contacts linked to a specific application
   */
  async getByApplicationId(applicationId: string): Promise<ContactRecord[]> {
    return contactRepository.getByApplicationId(applicationId)
  },

  /**
   * Search contacts by query
   */
  async search(query: string): Promise<ContactRecord[]> {
    if (!query.trim()) return this.getAll()
    return contactRepository.search(query)
  },

  /**
   * Get contacts with filters
   */
  async getFiltered(filters: ContactFilters): Promise<ContactRecord[]> {
    let contacts = await this.getAll()

    if (filters.search) {
      const q = filters.search.toLowerCase()
      contacts = contacts.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.role.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      )
    }

    if (filters.contactType) {
      contacts = contacts.filter(c => c.contactType === filters.contactType)
    }

    if (filters.company) {
      contacts = contacts.filter(c =>
        c.company?.toLowerCase().includes(filters.company!.toLowerCase())
      )
    }

    return contacts
  },

  /**
   * Create a new contact
   */
  async create(input: CreateContactInput): Promise<ContactRecord> {
    const id = await contactRepository.create({
      name: input.name,
      role: input.role,
      email: input.email,
      phone: input.phone,
      linkedInUrl: input.linkedInUrl,
      contactType: input.contactType,
      company: input.company,
      notes: input.notes || '',
      interactions: [],
      linkedApplicationIds: input.linkedApplicationIds || []
    })

    const contact = await contactRepository.getById(id)
    if (!contact) throw new Error('Failed to create contact')

    await this.queueSync(contact.id, 'create')
    return contact
  },

  /**
   * Update a contact
   */
  async update(id: string, updates: UpdateContactInput): Promise<ContactRecord> {
    await contactRepository.update(id, updates)

    const contact = await contactRepository.getById(id)
    if (!contact) throw new Error(`Contact ${id} not found`)

    await this.queueSync(id, 'update')
    return contact
  },

  /**
   * Delete a contact
   */
  async delete(id: string): Promise<void> {
    await contactRepository.delete(id)
    await this.queueSync(id, 'delete')
  },

  /**
   * Add an interaction to a contact
   */
  async addInteraction(contactId: string, interaction: Omit<InteractionRecord, 'id'>): Promise<void> {
    await contactRepository.addInteraction(contactId, interaction)
    await this.queueSync(contactId, 'update')
  },

  /**
   * Remove an interaction from a contact
   */
  async removeInteraction(contactId: string, interactionId: string): Promise<void> {
    await contactRepository.removeInteraction(contactId, interactionId)
    await this.queueSync(contactId, 'update')
  },

  /**
   * Link a contact to an application
   */
  async linkApplication(contactId: string, applicationId: string): Promise<void> {
    await contactRepository.linkApplication(contactId, applicationId)
    await this.queueSync(contactId, 'update')
  },

  /**
   * Unlink a contact from an application
   */
  async unlinkApplication(contactId: string, applicationId: string): Promise<void> {
    await contactRepository.unlinkApplication(contactId, applicationId)
    await this.queueSync(contactId, 'update')
  },

  // ---------------------------------------------------------------------------
  // Sync Helper
  // ---------------------------------------------------------------------------

  async queueSync(id: string, action: 'create' | 'update' | 'delete'): Promise<void> {
    const isAuthenticated = await authApi.isAuthenticated()
    if (!isAuthenticated) return

    const contact = action !== 'delete' ? await this.getById(id) : undefined

    await syncQueueRepository.add({
      entityType: 'contact',
      entityId: id,
      action,
      payload: contact ? { ...contact } as unknown as Record<string, unknown> : { id }
    })
  }
}
